import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Printer, Sparkles, HeartPulse, Baby, Droplets, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import { businessInfo } from '../data/mock';

// ── Reusable flyer shell ─────────────────────────────────────────────────────
const FlyerShell = ({ id, children, tone = 'cream' }) => {
  const bg = tone === 'dark' ? '#0F0E0C' : tone === 'lavender' ? '#E5DDF0' : '#F4EFE7';
  const fg = tone === 'dark' ? '#F4EFE7' : '#1E1B18';
  return (
    <div
      id={id}
      className="flyer-page relative mx-auto"
      style={{
        width: '8.5in',
        height: '11in',
        background: bg,
        color: fg,
        padding: '0.9in 0.8in',
        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
};

// ── Small helpers ────────────────────────────────────────────────────────────
const Corner = ({ children, className = '' }) => (
  <div className={`absolute top-8 left-8 right-8 flex items-center justify-between ${className}`}>
    {children}
  </div>
);

const Foot = ({ children }) => (
  <div className="absolute bottom-8 left-8 right-8 flex items-end justify-between">
    {children}
  </div>
);

const AddressBlock = ({ light = false }) => (
  <div className={`text-[11px] leading-relaxed ${light ? 'text-[#F4EFE7]/70' : 'text-[#1E1B18]/70'}`}>
    <div>{businessInfo.address.line1} {businessInfo.address.line2}</div>
    <div>{businessInfo.locationNote}</div>
    <div className="mt-1">{businessInfo.phone}</div>
  </div>
);

const QRBlock = ({ url, size = 130, label = 'Scan to book' }) => (
  <div className="flex flex-col items-end gap-1.5">
    <div className="bg-white p-2 rounded-sm">
      <QRCodeCanvas value={url} size={size} bgColor="#FFFFFF" fgColor="#0F0E0C" level="H" includeMargin={false} />
    </div>
    <div className="text-[10px] tracking-[0.28em] uppercase opacity-70">{label}</div>
  </div>
);

// ── Flyer 1: Grand opening ───────────────────────────────────────────────────
const GrandOpeningFlyer = ({ url }) => (
  <FlyerShell id="flyer-opening">
    <Corner>
      <div className="flex items-center gap-3">
        <Logo size={44} />
      </div>
      <span className="text-[10px] tracking-[0.35em] text-[#8B7FB8] font-medium">NOW BOOKING</span>
    </Corner>

    <div className="pt-24">
      <p className="text-[11px] tracking-[0.35em] text-[#8B7FB8] font-medium mb-6">01 &nbsp;—&nbsp; OPENING</p>
      <h1 className="font-serif leading-[0.95] mb-8" style={{ fontSize: '5rem' }}>
        A quieter kind<br />of therapy.
      </h1>
      <p className="text-[16px] leading-relaxed max-w-md text-[#1E1B18]/75">
        Registered Massage Therapy — Swedish, Lymphatic Drainage, and Prenatal care in a calm, welcoming space in the heart of Mississauga.
      </p>

      <div className="mt-16 grid grid-cols-2 gap-8 max-w-lg text-[13px]">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-2">SERVICES</div>
          <div>Swedish Massage</div>
          <div>Lymphatic Drainage</div>
          <div>Prenatal Massage</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-2">SESSIONS</div>
          <div>30 min · from $75</div>
          <div>60 min · from $120</div>
          <div className="text-[#1E1B18]/60 mt-1">Insurance receipts provided</div>
        </div>
      </div>
    </div>

    <Foot>
      <AddressBlock />
      <QRBlock url={url} />
    </Foot>
  </FlyerShell>
);

// ── Flyer 2: First-visit offer ───────────────────────────────────────────────
const FirstVisitFlyer = ({ url }) => (
  <FlyerShell id="flyer-offer" tone="dark">
    <Corner>
      <Logo size={44} />
      <span className="text-[10px] tracking-[0.35em] text-[#B9A9D6] font-medium">NEW CLIENT</span>
    </Corner>

    <div className="pt-24">
      <p className="text-[11px] tracking-[0.35em] text-[#B9A9D6] font-medium mb-6">A GENTLE WELCOME</p>
      <h1 className="font-serif leading-[0.95] mb-4 text-[#F4EFE7]" style={{ fontSize: '7rem' }}>
        $15 off
      </h1>
      <h2 className="font-serif text-4xl mb-8 text-[#F4EFE7]/90">your first session.</h2>
      <p className="text-[15px] leading-relaxed max-w-md text-[#F4EFE7]/75">
        Book any 60-minute massage — Swedish, Lymphatic Drainage, or Prenatal — and I'll take $15 off your first visit. My way of saying welcome to Aura Lune.
      </p>

      <div className="mt-14 max-w-md text-[12px] leading-relaxed text-[#F4EFE7]/60">
        Mention code <span className="text-[#B9A9D6] font-medium tracking-wider">WELCOME15</span> when booking.
        Valid for new clients only. One per person. Insurance receipts still provided.
      </div>
    </div>

    <Foot>
      <AddressBlock light />
      <QRBlock url={url} label="Scan · Book · Save $15" />
    </Foot>
  </FlyerShell>
);

// ── Flyer 3: Prenatal ────────────────────────────────────────────────────────
const PrenatalFlyer = ({ url }) => (
  <FlyerShell id="flyer-prenatal" tone="lavender">
    <Corner>
      <Logo size={44} />
      <span className="text-[10px] tracking-[0.35em] text-[#7B6BA6] font-medium">PRENATAL CARE</span>
    </Corner>

    <div className="pt-24">
      <p className="text-[11px] tracking-[0.35em] text-[#7B6BA6] font-medium mb-6">FOR EVERY TRIMESTER</p>
      <h1 className="font-serif leading-[0.95] mb-8" style={{ fontSize: '5.5rem' }}>
        Rest the<br />back you<br />can't feel<br />yet.
      </h1>
      <p className="text-[16px] leading-relaxed max-w-md text-[#1E1B18]/75">
        Safe, supported side-lying prenatal massage — pillows arranged, pressure adjusted, and pace guided by how you feel that day.
      </p>

      <div className="mt-12 space-y-2 text-[13px] max-w-md">
        <div className="flex items-center gap-3"><Baby size={15} className="text-[#7B6BA6]" strokeWidth={1.6} /> Trimester-safe positioning</div>
        <div className="flex items-center gap-3"><HeartPulse size={15} className="text-[#7B6BA6]" strokeWidth={1.6} /> Relief for hips, lower back & shoulders</div>
        <div className="flex items-center gap-3"><Sparkles size={15} className="text-[#7B6BA6]" strokeWidth={1.6} /> Insurance receipts provided</div>
      </div>
    </div>

    <Foot>
      <AddressBlock />
      <QRBlock url={url} />
    </Foot>
  </FlyerShell>
);

// ── Flyer 4: Lymphatic ───────────────────────────────────────────────────────
const LymphaticFlyer = ({ url }) => (
  <FlyerShell id="flyer-lymphatic">
    <Corner>
      <Logo size={44} />
      <span className="text-[10px] tracking-[0.35em] text-[#8B7FB8] font-medium">LYMPHATIC DRAINAGE</span>
    </Corner>

    <div className="pt-24">
      <p className="text-[11px] tracking-[0.35em] text-[#8B7FB8] font-medium mb-6">POST-SURGERY & WELLNESS</p>
      <h1 className="font-serif leading-[0.95] mb-8" style={{ fontSize: '5.5rem' }}>
        Slow, quiet,<br />and exactly<br />what your<br />body asked for.
      </h1>
      <p className="text-[16px] leading-relaxed max-w-md text-[#1E1B18]/75">
        Gentle rhythmic strokes designed to move fluid, reduce swelling, and calm the nervous system — ideal after surgery or in seasons of stress.
      </p>

      <div className="mt-12 space-y-2 text-[13px] max-w-md">
        <div className="flex items-center gap-3"><Droplets size={15} className="text-[#7B6BA6]" strokeWidth={1.6} /> Reduces post-op swelling</div>
        <div className="flex items-center gap-3"><HeartPulse size={15} className="text-[#7B6BA6]" strokeWidth={1.6} /> Supports immunity & recovery</div>
        <div className="flex items-center gap-3"><Sparkles size={15} className="text-[#7B6BA6]" strokeWidth={1.6} /> Direct billing eligible</div>
      </div>

      <div className="mt-12 text-[13px] text-[#1E1B18]/60 max-w-md">
        30 min · $85 &nbsp;·&nbsp; 60 min · $130
      </div>
    </div>

    <Foot>
      <AddressBlock />
      <QRBlock url={url} />
    </Foot>
  </FlyerShell>
);

// ── Flyer 5: Referral ────────────────────────────────────────────────────────
const ReferralFlyer = ({ url }) => (
  <FlyerShell id="flyer-referral">
    <Corner>
      <Logo size={44} />
      <span className="text-[10px] tracking-[0.35em] text-[#8B7FB8] font-medium">REFER A FRIEND</span>
    </Corner>

    <div className="pt-24">
      <p className="text-[11px] tracking-[0.35em] text-[#8B7FB8] font-medium mb-6">SHARE THE CALM</p>
      <h1 className="font-serif leading-[0.95] mb-6" style={{ fontSize: '6rem' }}>
        Send a friend,<br />you both<br />save 15%.
      </h1>
      <p className="text-[16px] leading-relaxed max-w-md text-[#1E1B18]/75">
        When someone books their first session with Aura Lune and mentions your name, you'll both get 15% off your next visit. That's it — no forms, no cards.
      </p>

      <div className="mt-14 grid grid-cols-3 gap-4 max-w-lg">
        <div className="p-4 border border-[#1E1B18]/10 rounded-sm">
          <div className="text-[10px] tracking-[0.25em] text-[#8B7FB8] font-medium mb-1">01</div>
          <div className="text-[13px]">Tell a friend about Aura Lune</div>
        </div>
        <div className="p-4 border border-[#1E1B18]/10 rounded-sm">
          <div className="text-[10px] tracking-[0.25em] text-[#8B7FB8] font-medium mb-1">02</div>
          <div className="text-[13px]">They book, mention your name</div>
        </div>
        <div className="p-4 border border-[#1E1B18]/10 rounded-sm">
          <div className="text-[10px] tracking-[0.25em] text-[#8B7FB8] font-medium mb-1">03</div>
          <div className="text-[13px]">You both save 15% next visit</div>
        </div>
      </div>
    </div>

    <Foot>
      <div className="flex items-center gap-2 text-[11px] text-[#1E1B18]/70">
        <Users size={13} strokeWidth={1.6} /> {businessInfo.phone}
      </div>
      <QRBlock url={url} label="Scan to share" />
    </Foot>
  </FlyerShell>
);

// ── Main page ────────────────────────────────────────────────────────────────
const flyers = [
  { key: 'opening', label: 'Grand Opening', desc: 'A calm launch card for signage, cafes, and pinboards.', Comp: GrandOpeningFlyer },
  { key: 'offer', label: '$15 Off First Visit', desc: 'Dark hero for social posts and paid promo.', Comp: FirstVisitFlyer },
  { key: 'prenatal', label: 'Prenatal Care', desc: 'Warm lavender card — great for OBGYN and midwife offices.', Comp: PrenatalFlyer },
  { key: 'lymphatic', label: 'Lymphatic Drainage', desc: 'For chiropractors, PTs, and post-op clinics.', Comp: LymphaticFlyer },
  { key: 'referral', label: 'Refer a Friend', desc: 'Reception copy — leave a stack at the spa.', Comp: ReferralFlyer },
];

const Flyers = () => {
  const [url, setUrl] = useState('');
  const [active, setActive] = useState('opening');
  const wrapRef = useRef(null);

  useEffect(() => {
    setUrl(window.location.origin);
  }, []);

  const runPrint = (key /* or 'all' */) => {
    const cls = key === 'all' ? 'print-all' : `print-only-${key}`;
    document.body.classList.add(cls);
    // Give the browser a paint tick to apply the class before opening the dialog
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove(cls), 500);
    }, 80);
  };

  return (
    <div className="bg-[#F4EFE7] min-h-screen">
      <Navbar />

      {/* Print rules — only the requested flyer(s) print, at true 8.5x11 each on its own page */}
      <style>{`
        /* On-screen preview: shrink each flyer so it fits nicely on the page */
        .flyer-scale {
          width: calc(8.5in * 0.72);
          height: calc(11in * 0.72);
          overflow: hidden;
          display: inline-block;
        }
        .flyer-scale > .flyer-page {
          transform: scale(0.72);
          transform-origin: top left;
        }

        @media print {
          @page { size: 8.5in 11in; margin: 0; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          /* Hide everything by default when printing */
          body * { visibility: hidden !important; }
          /* Force site chrome off */
          header, footer, .flyers-toolbar, .flyers-intro { display: none !important; }

          /* Remove the preview scaling for printing */
          .flyer-scale {
            width: 8.5in !important;
            height: 11in !important;
            overflow: visible !important;
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .flyer-scale > .flyer-page {
            transform: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: always;
            break-after: page;
          }
          /* Show only the flyers we want to print */
          .flyer-page, .flyer-page * { visibility: visible !important; }
          .section-flyer { display: block !important; page-break-inside: avoid; }

          /* Single-flyer mode: hide all others */
          ${flyers.map((f) => `
            body.print-only-${f.key} .section-flyer:not([data-flyer="${f.key}"]) { display: none !important; }
            body.print-only-${f.key} .section-flyer[data-flyer="${f.key}"] .flyer-page { page-break-after: auto; break-after: auto; }
          `).join('\n')}
        }
      `}</style>

      <section className="pt-32 pb-16 max-w-[1400px] mx-auto px-8 flyers-intro">
        <p className="text-[11px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-6">MARKETING</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#1E1B18] leading-[1.05] mb-4">
          Ready-to-print flyers
        </h1>
        <p className="text-[15px] text-[#1E1B18]/70 leading-relaxed max-w-2xl">
          Five designs, all sized for standard letter paper (8.5″ × 11″), each with your booking QR code baked in.
          Pick one, click <em>Print</em>, then choose <strong>"Save as PDF"</strong> or send straight to a printer.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-2 flyers-toolbar">
          {flyers.map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setActive(f.key);
                document.getElementById(`section-${f.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`px-4 py-2 rounded-full text-[12px] font-medium border transition-all ${
                active === f.key
                  ? 'bg-[#0F0E0C] text-[#F4EFE7] border-[#0F0E0C]'
                  : 'bg-transparent text-[#1E1B18] border-[#1E1B18]/20 hover:border-[#1E1B18]/50'
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => runPrint('all')}
            className="ml-2 inline-flex items-center gap-2 bg-[#B9A9D6] hover:bg-[#A896C9] text-[#1E1B18] px-4 py-2 rounded-full text-[12px] font-medium transition-colors"
          >
            <Printer size={13} /> Print all as PDF
          </button>
        </div>

        <div className="mt-6 text-[12px] text-[#1E1B18]/60 leading-relaxed max-w-2xl flyers-toolbar">
          <strong className="text-[#1E1B18]">Print tip:</strong> in the browser dialog, set <em>Margins</em> to
          "None", <em>Scale</em> to "Default 100%", and turn on "Background graphics" so the dark and lavender flyers print with their colours.
        </div>
      </section>

      <section ref={wrapRef} className="pb-24 max-w-[1400px] mx-auto px-8 space-y-16">
        {flyers.map(({ key, label, desc, Comp }) => (
          <div key={key} id={`section-${key}`} className="section-flyer" data-flyer={key}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 flyers-toolbar">
              <div>
                <div className="text-[11px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-1">FLYER · {label.toUpperCase()}</div>
                <div className="text-[14px] text-[#1E1B18]/70 max-w-xl">{desc}</div>
              </div>
              <button
                onClick={() => runPrint(key)}
                className="inline-flex items-center gap-2 bg-[#0F0E0C] hover:bg-[#2A2622] text-[#F4EFE7] px-5 py-2.5 rounded-full text-[13px] font-medium transition-colors self-start md:self-auto"
              >
                <Printer size={14} /> Print this flyer
              </button>
            </div>
            <div className="flyer-scale">
              <Comp url={url || 'https://aura-lune.example'} />
            </div>
          </div>
        ))}
      </section>

      <Footer />
    </div>
  );
};

export default Flyers;
